import React, { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import SheetHeader from 'ui/Sheet/SheetHeader';
import Title from 'ui/Typography/Title';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import Body from 'ui/Typography/Body';
import Sheet from 'ui/Sheet/Sheet';
import {
  KompisSheetFooterButtons,
  KompisSheetFooter,
  KompisSheetBody,
  KompisSheetBodyPadding
} from '@inter-ikea-kompis/react-components';
import Box from 'ui/Box';
import styled from 'styled-components';
import { useAppDispatch } from 'ducks/hooks';
import LightbulbIncandescent from './LightbulbIncandescent.svg?react';
import LightbulbWithRays from './LightbulbWithRays.svg?react';
import { useCore } from 'contexts/core';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import {
  hideTooManyLightsSheet,
  isModalVisible,
  setDismissed,
  showTooManyLightsSheet,
  wasDismissed
} from './TooManyLightsFeature';

const StyledTitle = styled(Title)`
  margin-bottom: 2rem;
`;

const LightbulbList = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 40px);
  justify-content: center;
  padding: 2rem 0;
  grid-column-gap: 20px;
  grid-row-gap: 20px;
`;

const StyledLightbulbWithRays = styled(LightbulbWithRays)`
  width: 40px;
`;

const StyledLightbulbIncandescent = styled(LightbulbIncandescent)`
  width: 40px;
`;

const TooManyLightsWarningSheet = () => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const visible = !!useSelector(isModalVisible);
  const dismissed = useSelector(wasDismissed);
  const hasShownTooManyLightsSheetRef = useRef(dismissed);
  const coreApi = useCore();

  const handleTooManyLightsAdded = () => {
    if (!hasShownTooManyLightsSheetRef.current) {
      dispatch(showTooManyLightsSheet());
    }
  };

  useEffect(() => {
    hasShownTooManyLightsSheetRef.current = dismissed;
  }, [dismissed]);

  useEffect(() => {
    return coreApi.subscribe('tooManyLightsAdded', handleTooManyLightsAdded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClose = () => {
    dispatch(hideTooManyLightsSheet());
  };

  const onButtonClick = () => {
    dispatch(setDismissed(true));
  };

  return (
    <Sheet onOverlayClick={onClose} visible={visible}>
      <SheetHeader backIcon={true} onBackButtonClick={onClose} onCloseButtonClick={onClose} />
      <KompisSheetBody theme={SkapaTheme}>
        <KompisSheetBodyPadding theme={SkapaTheme}>
          <StyledTitle type={ThemeFontStyleTypeEnum.headingMedium}>
            {formatMessage({ id: 'storageone.nightLightsLightSourceLimitsSheetTitle' })}
          </StyledTitle>
          <Body type={ThemeFontStyleTypeEnum.bodyLarge}>
            {formatMessage({ id: 'storageone.nightLightsLightSourceLimitsSheetPreamble' })}
          </Body>
          <Box mt={'l'}>
            <LightbulbList>
              <StyledLightbulbWithRays />
              <StyledLightbulbWithRays />
              <StyledLightbulbWithRays />
              <StyledLightbulbWithRays />
              <StyledLightbulbIncandescent />
              <StyledLightbulbWithRays />
              <StyledLightbulbWithRays />
              <StyledLightbulbWithRays />
              <StyledLightbulbIncandescent />
              <StyledLightbulbIncandescent />
            </LightbulbList>
          </Box>
        </KompisSheetBodyPadding>
      </KompisSheetBody>
      <KompisSheetFooter theme={SkapaTheme}>
        <KompisSheetFooterButtons
          theme={SkapaTheme}
          primaryLabel={formatMessage({
            id: 'storageone.nightLightsLightSourceLimitsSheetButton'
          })}
          onButtonClick={() => onButtonClick()}
        />
      </KompisSheetFooter>
    </Sheet>
  );
};

export default TooManyLightsWarningSheet;
